import { Box, FormControl, Stack, TextField, Typography } from '@mui/material';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';
import HelperTextContainer from '@/components/HelperTextContainer';
import { PipelineSchema } from '@/zod_schemas';

interface PipelineDetailsFormProps {
  register: UseFormRegister<PipelineSchema>;
  control: Control<PipelineSchema>;
  errors: FieldErrors<PipelineSchema>;
  mode: string;
}

const PipelineDetailsForm = ({
  register,
  control,
  errors,
  mode,
}: PipelineDetailsFormProps) => (
  <Box
    sx={{
      p: { xs: 2, md: 2.5 },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction="column" spacing={3}>
      <Typography variant="h3" gutterBottom>
        Details
      </Typography>
      <Stack spacing={4} direction={{ xs: 'column', md: 'row' }}>
        <FormControl fullWidth margin="normal">
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                variant="outlined"
                fullWidth
                {...register('name')}
                sx={{
                  bgcolor: 'background.paper',
                }}
              />
            )}
          />
          <HelperTextContainer>
            <span>{errors.name?.message}</span>
          </HelperTextContainer>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <Controller
            name="description"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                variant="outlined"
                fullWidth
                {...register('description')}
                sx={{
                  bgcolor: 'background.paper',
                }}
              />
            )}
          />
          <HelperTextContainer>
            <span>{errors.description?.message}</span>
          </HelperTextContainer>
        </FormControl>
      </Stack>
      {mode !== 'create' && (
        <Stack spacing={4} direction={{ xs: 'column', md: 'row' }}>
          <FormControl fullWidth margin="normal">
            <Controller
              name="created_at"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    bgcolor: 'background.default',
                    color: 'text.secondary',
                    '& .MuiInputBase-input': {
                      cursor: 'not-allowed',
                    },
                  }}
                  {...field}
                  variant="outlined"
                  label="Created At"
                  fullWidth
                  {...register('created_at')}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <Controller
              name="updated_at"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    bgcolor: 'background.default',
                    color: 'text.secondary',
                    '& .MuiInputBase-input': {
                      cursor: 'not-allowed',
                    },
                  }}
                  {...field}
                  variant="outlined"
                  fullWidth
                  label="Updated At"
                  {...register('updated_at')}
                />
              )}
            />
          </FormControl>
        </Stack>
      )}
    </Stack>
  </Box>
);

export default PipelineDetailsForm;
